import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { LibraryHeader } from '../components/PageComponents/header/LibraryHeader';
import { PlaylistCard } from '../components/PageComponents/cards/PlaylistCard';
import FilterBar, { readFilters, type FilterSpec, type FilterState } from '../components/PageComponents/filters/FilterBar';
import { PlaylistsContext } from '../context/PlaylistsContext';
import { PlayerContext } from '../context/PlayerContext';
import '../components/PageComponents/cards/Cards.css';
import './Playlists.css';

const PLAYLIST_FILTER_SPEC: FilterSpec = {
    defaultSort: 'id',
    defaultOrder: 'asc',
    sorts: [
        { value: 'id', label: 'Default' },
        { value: 'name', label: 'Name' },
        { value: 'created', label: 'Newest' },
    ],
    selects: [],
};

export default function Playlists(): React.JSX.Element {
    const navigate = useNavigate();
    const { playlists, loading, createPlaylist, getPlaylistWithTracks } = useContext(PlaylistsContext);
    const { tracks, refreshTracks, setQueue } = useContext(PlayerContext);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Client-side filter/search over the caller's playlists; playlists are
    // a small per-user list, so no server round-trip is needed.
    const [filterState, setFilterState] = useState<FilterState>(() => readFilters(PLAYLIST_FILTER_SPEC));
    const filteredPlaylists = useMemo(() => {
        const q = filterState.q.trim().toLowerCase();
        let list = playlists;
        if (q) {
            list = list.filter(
                (p) =>
                    p.name.toLowerCase().includes(q) ||
                    (p.description ?? '').toLowerCase().includes(q),
            );
        }
        const dir = filterState.order === 'desc' ? -1 : 1;
        const sorted = [...list].sort((a, b) => {
            switch (filterState.sort) {
                case 'name':
                    return dir * a.name.localeCompare(b.name);
                case 'created':
                    return dir * (b.id - a.id);
                default:
                    return dir * (a.id - b.id);
            }
        });
        return sorted;
    }, [playlists, filterState]);

    useEffect(() => {
        refreshTracks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSubmitting(true);
        const playlist = await createPlaylist(name.trim(), description.trim());
        setSubmitting(false);
        if (playlist) {
            setName('');
            setDescription('');
            setCreating(false);
            navigate(`/playlists/${playlist.id}`);
        }
    };

    const handlePlay = async (playlistId: number) => {
        const withTracks = await getPlaylistWithTracks(playlistId);
        if (!withTracks) return;
        const resolved = withTracks.trackIds
            .map((trackId) => tracks.find((t) => t.getId() === trackId))
            .filter((t): t is NonNullable<typeof t> => Boolean(t));
        if (resolved.length > 0) setQueue(resolved, 0);
    };

    return (
        <>
            <div className="pageHeader">
                <div className="playlistsHeaderRow">
                    <LibraryHeader eyebrow="Your library" title="Playlists" />
                    <button className="newPlaylistButton" onClick={() => setCreating((c) => !c)}>
                        <Plus size={16} strokeWidth={2.75} />
                        New Playlist
                    </button>
                </div>
            </div>
            <div className="pageContent">
                <FilterBar spec={PLAYLIST_FILTER_SPEC} state={filterState} onChange={setFilterState} resultCount={filteredPlaylists.length} />
                {creating && (
                    <form className="newPlaylistForm" onSubmit={handleCreate}>
                        <input
                            className="newPlaylistInput"
                            type="text"
                            placeholder="Playlist name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={60}
                            autoFocus
                        />
                        <input
                            className="newPlaylistInput"
                            type="text"
                            placeholder="Description (optional)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={255}
                        />
                        <div className="newPlaylistActions">
                            <button type="button" className="newPlaylistCancel" onClick={() => setCreating(false)}>
                                Cancel
                            </button>
                            <button type="submit" className="newPlaylistSubmit" disabled={!name.trim() || submitting}>
                                {submitting ? 'Creating…' : 'Create'}
                            </button>
                        </div>
                    </form>
                )}

                {loading ? (
                    <div className="trackListEmpty">Loading playlists…</div>
                ) : filteredPlaylists.length === 0 ? (
                    <div className="trackListEmpty">No playlists yet - create one to get started</div>
                ) : (
                    <div className="cardGrid">
                        {filteredPlaylists.map((playlist) => (
                            <PlaylistCard
                                key={playlist.id}
                                playlist={playlist}
                                onClick={(p) => navigate(`/playlists/${p.id}`)}
                                onPlay={(p) => handlePlay(p.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
